import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FaEye, FaPlus, FaRedo, FaInfoCircle, FaTimes, FaCheckCircle, FaPaperPlane } from 'react-icons/fa';
import axios from '../axiosInstance';
import { UserContext } from '../UserContext';
import { useDialog } from '../components/ConfirmProvider';
import Pagination from '../components/Pagination';
import { formatDateTime } from '../utils/dateFormat';
import '../Pips/Pips.css';
import '../Pages.css';
import '../Customers/Customers.css';
import './OrgMessages.css';

// Messages to organisations — PIP Intel staff email organisation managers
// (users with org_messages.receive): every organisation, or those picked by
// organisation, package, billing status or active status. Sent messages are
// kept and can be viewed and resent. The API enforces the same rules.

const BILLING_STATUSES = [
  ['any', 'Any billing status'],
  ['paid_up', 'Paid up (nothing unpaid)'],
  ['outstanding', 'Has an unpaid invoice'],
  ['overdue', 'Has an overdue invoice'],
  ['suspended', 'Suspended for non-payment']
];
const ORG_STATUSES = [['all', 'Active and inactive'], ['active', 'Active only'], ['inactive', 'Inactive only']];
const EMPTY_AUDIENCE = { organisation_ids: [], package_ids: [], billing_status: 'any', org_status: 'all' };

const hasPerm = (user, name) =>
  !!user && (user.is_system_admin === true || (Array.isArray(user.permissions) && user.permissions.includes(name)));
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
const errorText = (err, fallback) => {
  const data = err.response?.data;
  return (typeof data?.error === 'string' && data.error) || data?.error?.message || err.message || fallback;
};
const toggle = (list, id) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

function OrgMessages() {
  const { user } = useContext(UserContext);
  const { confirm } = useDialog();
  const isStaff = !!user && (user.is_system_admin === true || !user.organisation_id);
  const canSend = isStaff && hasPerm(user, 'org_messages.send');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [detail, setDetail] = useState(null);     // message with sends
  const [compose, setCompose] = useState(null);   // { subject, body, audience }
  const [options, setOptions] = useState(null);   // { packages, organisations }
  const [preview, setPreview] = useState(null);   // { label, organisations }
  const [orgSearch, setOrgSearch] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/org-messages');
      setRows(Array.isArray(res.data) ? res.data : []);
      setError(null);
    } catch (err) {
      setError(errorText(err, 'Could not load messages.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isStaff) fetchAll(); }, [isStaff, fetchAll]);

  // Live audience preview while composing.
  const audienceKey = compose ? JSON.stringify(compose.audience) : null;
  useEffect(() => {
    if (!audienceKey) return undefined;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await axios.post('/org-messages/preview', { audience: JSON.parse(audienceKey) });
        if (!cancelled) setPreview(res.data);
      } catch (err) {
        if (!cancelled) setPreview({ label: '', organisations: [], error: errorText(err, 'Could not work out the audience.') });
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [audienceKey]);

  const openDetail = async (id) => {
    try {
      const res = await axios.get(`/org-messages/${id}`);
      setDetail({ sends: [], ...res.data });
    } catch (err) {
      setError(errorText(err, 'Could not open the message.'));
    }
  };

  const openCompose = async (from) => {
    try {
      if (!options) setOptions((await axios.get('/org-messages/options')).data);
      setPreview(null);
      setOrgSearch('');
      setCompose(from
        ? { subject: from.subject, body: from.body, audience: { ...EMPTY_AUDIENCE, ...(from.audience || {}) } }
        : { subject: '', body: '', audience: { ...EMPTY_AUDIENCE } });
    } catch (err) {
      setError(errorText(err, 'Could not load the organisations.'));
    }
  };

  const setAudience = (patch) => setCompose((c) => ({ ...c, audience: { ...c.audience, ...patch } }));

  const outcome = (r) => `${plural(r.sent, 'organisation')} emailed` +
    (r.failed ? `, ${r.failed} failed (see the message for why)` : '') + '.';

  const submitCompose = async (e) => {
    e.preventDefault();
    const count = preview?.organisations?.length || 0;
    if (!count) { setError('No organisation matches the audience you chose.'); return; }
    const managers = preview.organisations.reduce((s, o) => s + o.recipients.length, 0);
    const ok = await confirm({
      title: 'Send message',
      message: `Email "${compose.subject.trim()}" to the managers of ${plural(count, 'organisation')} (${plural(managers, 'manager')})?`,
      confirmLabel: 'Send'
    });
    if (!ok) return;
    setBusy(true);
    try {
      const res = await axios.post('/org-messages', compose);
      setCompose(null);
      setError(null);
      setNotice(`Message "${res.data.message.subject}" sent: ${outcome(res.data.result)}`);
      await fetchAll();
    } catch (err) {
      setError(errorText(err, 'The message was not sent.'));
    } finally {
      setBusy(false);
    }
  };

  const resend = async (msg, target) => {
    const ok = await confirm({
      title: target === 'failed' ? 'Resend to failed deliveries' : 'Resend to everyone',
      message: target === 'failed'
        ? `Resend "${msg.subject}" to the organisations it could not be delivered to?`
        : `Resend "${msg.subject}" to its audience as it is now (${msg.audience_label})?`,
      confirmLabel: 'Resend'
    });
    if (!ok) return;
    setBusy(true);
    try {
      const res = await axios.post(`/org-messages/${msg.id}/resend`, { target });
      setError(null);
      setNotice(`Message "${msg.subject}" resent: ${outcome(res.data.result)}`);
      if (detail && detail.id === msg.id) setDetail({ sends: [], ...res.data.message });
      await fetchAll();
    } catch (err) {
      setError(errorText(err, 'The message was not resent.'));
    } finally {
      setBusy(false);
    }
  };

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return rows.filter((r) => !q || [r.subject, r.audience_label, r.created_by_name]
      .some((v) => String(v || '').toLowerCase().includes(q)));
  }, [rows, searchTerm]);
  const page = Math.min(currentPage, Math.max(1, Math.ceil(filtered.length / itemsPerPage)));
  const pageItems = filtered.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  if (!isStaff) {
    return (
      <div className="page-container">
        <div className="alert-banner alert-banner--warning" style={{ marginTop: '2rem' }}>
          <span className="alert-banner__body"><FaInfoCircle /> Messages are only available to PIP Intel staff.</span>
        </div>
      </div>
    );
  }

  const muted = { fontSize: '0.75rem', color: 'var(--text-secondary,#607d8b)' };
  const totalDelivered = rows.reduce((s, r) => s + r.delivered, 0);
  const totalFailed = rows.reduce((s, r) => s + r.failed, 0);
  const orgChoices = (options?.organisations || [])
    .filter((o) => !orgSearch.trim() || o.name.toLowerCase().includes(orgSearch.trim().toLowerCase()));
  const noManager = (preview?.organisations || []).filter((o) => !o.recipients.length);

  return (
    <div className="page-container">
      <div className="stats-section">
        <div className="stats-layout__rest">
          <div className="stats-grid">
            <div className="stat-card stat-card--blue">
              <div className="stat-label">Messages</div>
              <div className="stat-value stat-value--blue">{rows.length}</div>
            </div>
            <div className="stat-card stat-card--purple">
              <div className="stat-label">Delivered</div>
              <div className="stat-value stat-value--purple">{totalDelivered}</div>
              <div style={{ ...muted, marginTop: '0.35rem' }}>organisations, latest send</div>
            </div>
            <div className="stat-card stat-card--orange">
              <div className="stat-label">Failed</div>
              <div className="stat-value stat-value--orange">{totalFailed}</div>
              <div style={{ ...muted, marginTop: '0.35rem' }}>can be resent</div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ margin: '1rem 0 2rem' }}>
        <div className="table-controls">
          <div style={{ position: 'relative', flex: '1 1 auto', maxWidth: '300px' }}>
            <input
              type="text"
              placeholder="Search messages..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              className="search-filter"
              style={{ width: '100%', maxWidth: 'none', boxSizing: 'border-box' }}
            />
          </div>
          {canSend && (
            <div className="button-group">
              <button className="export-button" onClick={() => openCompose(null)} disabled={busy}><FaPlus style={{ marginRight: 5 }} /> New message</button>
            </div>
          )}
        </div>
      </div>

      {notice && (
        <div className="alert-banner alert-banner--success">
          <span className="alert-banner__body" style={{ flex: 1 }}><FaCheckCircle /> {notice}</span>
          <span role="button" tabIndex={0} title="Dismiss" style={{ cursor: 'pointer' }} onClick={() => setNotice(null)}><FaTimes /></span>
        </div>
      )}
      {error && (
        <div className="alert-banner alert-banner--danger">
          <span className="alert-banner__body" style={{ flex: 1 }}><FaInfoCircle /> {error}</span>
          <span role="button" tabIndex={0} title="Dismiss" style={{ cursor: 'pointer' }} onClick={() => setError(null)}><FaTimes /></span>
        </div>
      )}

      {loading && <p>Loading messages…</p>}
      {!loading && rows.length === 0 && !error && (
        <p style={{ color: 'var(--text-secondary)' }}>No messages sent yet. Use New message to email organisation managers.</p>
      )}

      {!loading && rows.length > 0 && (
        <>
          <div className="table-container">
            <table className="pips-table">
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>Sent to</th>
                  <th>Organisations</th>
                  <th>Delivery</th>
                  <th>Sent by</th>
                  <th>Last sent</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((r) => (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 600 }}>{r.subject}</td>
                    <td>{r.audience_label}</td>
                    <td>{r.organisations}</td>
                    <td>
                      <span className="un-meta__pill cust-pill--status-active">{r.delivered} delivered</span>
                      {r.failed > 0 && <div><span className="un-meta__pill cust-pill--status-rejected">{r.failed} failed</span></div>}
                    </td>
                    <td>{r.created_by_name || '—'}</td>
                    <td style={muted}>
                      {formatDateTime(r.last_sent_at || r.created_at)}
                      {r.rounds > 1 && <div>sent {r.rounds} times</div>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap', maxWidth: 'none', overflow: 'visible', textOverflow: 'clip' }}>
                      <button title="View" className="action-button" onClick={() => openDetail(r.id)}><FaEye /></button>
                      {canSend && r.failed > 0 && (
                        <button title="Resend to failed deliveries" className="action-button" disabled={busy} onClick={() => resend(r, 'failed')}><FaRedo /></button>
                      )}
                      {canSend && (
                        <button title="Send again as a new message (edit first)" className="action-button" disabled={busy} onClick={() => openCompose(r)}><FaPaperPlane /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={page}
            totalItems={filtered.length}
            itemsPerPage={itemsPerPage}
            itemLabel="message"
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
          />
        </>
      )}

      {/* ── Message details ── */}
      {detail && (
        <div className="modal-overlay" onClick={() => !busy && setDetail(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 820 }}>
            <button className="modal-close" onClick={() => !busy && setDetail(null)}>×</button>
            <h2 style={{ marginTop: 0 }}>{detail.subject}</h2>
            <div className="form-grid-2" style={{ fontSize: '0.9rem' }}>
              <div><div style={muted}>Sent to</div>{detail.audience_label}</div>
              <div>
                <div style={muted}>Sent</div>
                {formatDateTime(detail.created_at)}{detail.created_by_name ? ` by ${detail.created_by_name}` : ''}
                {detail.rounds > 1 && <span style={muted}> · sent {detail.rounds} times</span>}
              </div>
            </div>
            <h3 style={{ marginBottom: '0.5rem' }}>Message</h3>
            <div style={{ whiteSpace: 'pre-wrap', padding: '0.75rem 1rem', border: '1px solid var(--border-color, #dfe3e8)', fontSize: '0.9rem' }}>
              {detail.body}
            </div>
            <h3 style={{ marginBottom: '0.5rem' }}>Deliveries</h3>
            {detail.sends.length === 0 ? (
              <p style={{ color: 'var(--text-secondary)' }}>No deliveries recorded.</p>
            ) : (
              <div className="table-container">
                <table className="pips-table">
                  <thead><tr><th>Send</th><th>Organisation</th><th>To</th><th>Result</th></tr></thead>
                  <tbody>
                    {detail.sends.map((s) => (
                      <tr key={s.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {s.round === 1 ? 'First send' : `Resend ${s.round - 1}`}
                          <div style={muted}>{formatDateTime(s.sent_at)}{s.sent_by_name ? ` · ${s.sent_by_name}` : ''}</div>
                        </td>
                        <td>{s.organisation_name}</td>
                        <td>{s.recipients.join(', ') || '—'}</td>
                        <td>{s.success ? 'Delivered' : <span style={{ color: '#b71c1c' }}>Failed: {s.error}</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {canSend && (
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button className="btn-cancel" disabled={busy} onClick={() => resend(detail, 'failed')}><FaRedo style={{ marginRight: 5 }} /> Resend to failed</button>
                <button className="btn-cancel" disabled={busy} onClick={() => resend(detail, 'all')}><FaRedo style={{ marginRight: 5 }} /> Resend to everyone</button>
                <button className="export-button" disabled={busy} onClick={() => { const d = detail; setDetail(null); openCompose(d); }}>
                  <FaPaperPlane style={{ marginRight: 5 }} /> Edit and send as new
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── New message ── */}
      {compose && (
        <div className="modal-overlay" onClick={() => !busy && setCompose(null)}>
          <form className="modal-content" onClick={(e) => e.stopPropagation()} onSubmit={submitCompose} style={{ maxWidth: 860 }}>
            <button type="button" className="modal-close" onClick={() => !busy && setCompose(null)}>×</button>
            <h2 style={{ marginTop: 0 }}>New message to organisation managers</h2>

            <div className="form-group">
              <label>Subject</label>
              <input type="text" required minLength={3} maxLength={200} value={compose.subject}
                onChange={(e) => setCompose((c) => ({ ...c, subject: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Message</label>
              <textarea rows={8} required minLength={3} maxLength={20000} value={compose.body}
                onChange={(e) => setCompose((c) => ({ ...c, body: e.target.value }))} />
              <div style={muted}>
                Starts with "Dear &lt;organisation&gt; team," and ends with "Kind regards". Leave a blank line between paragraphs;
                {' '}{'{{organisation}}'} is replaced with each organisation's name.
              </div>
            </div>

            <h3 style={{ marginBottom: '0.5rem' }}>Send to</h3>
            <div className="form-grid-2">
              <div className="form-group">
                <label>Billing status</label>
                <select value={compose.audience.billing_status} onChange={(e) => setAudience({ billing_status: e.target.value })}>
                  {BILLING_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Organisation status</label>
                <select value={compose.audience.org_status} onChange={(e) => setAudience({ org_status: e.target.value })}>
                  {ORG_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Packages <span style={muted}>(none ticked = every package)</span></label>
              <div className="msg-checks">
                {(options?.packages || []).map((p) => (
                  <label key={p.id} className="msg-check">
                    <input type="checkbox" checked={compose.audience.package_ids.includes(p.id)}
                      onChange={() => setAudience({ package_ids: toggle(compose.audience.package_ids, p.id) })} />
                    {p.name}
                  </label>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label>
                Organisations <span style={muted}>(none ticked = every organisation matching the filters above)</span>
                {compose.audience.organisation_ids.length > 0 && (
                  <button type="button" className="btn-cancel" style={{ marginLeft: 10, padding: '2px 8px' }}
                    onClick={() => setAudience({ organisation_ids: [] })}>Clear ({compose.audience.organisation_ids.length})</button>
                )}
              </label>
              <input type="text" placeholder="Find an organisation..." value={orgSearch} onChange={(e) => setOrgSearch(e.target.value)} />
              <div className="msg-checklist">
                {orgChoices.map((o) => (
                  <label key={o.id} className="msg-check">
                    <input type="checkbox" checked={compose.audience.organisation_ids.includes(o.id)}
                      onChange={() => setAudience({ organisation_ids: toggle(compose.audience.organisation_ids, o.id) })} />
                    <span className="msg-check__name">{o.name}</span>
                    <span className="msg-check__meta">{o.package_name || 'No package'}{o.is_active ? '' : ' · inactive'}</span>
                  </label>
                ))}
                {orgChoices.length === 0 && <span className="msg-checklist__empty">No organisation matches.</span>}
              </div>
            </div>

            <div className={`alert-banner ${preview?.organisations?.length ? 'alert-banner--info' : 'alert-banner--warning'}`}>
              <span className="alert-banner__body">
                <FaInfoCircle />{' '}
                {!preview ? 'Working out who this goes to…'
                  : preview.error ? preview.error
                    : preview.organisations.length === 0 ? 'No organisation matches — nothing would be sent.'
                      : <>
                        <strong>{preview.label}</strong>: {plural(preview.organisations.length, 'organisation')},
                        {' '}{plural(preview.organisations.reduce((s, o) => s + o.recipients.length, 0), 'manager')}
                        {' '}— {preview.organisations.slice(0, 8).map((o) => o.name).join(', ')}
                        {preview.organisations.length > 8 ? ` and ${preview.organisations.length - 8} more` : ''}.
                        {noManager.length > 0 && ` ${noManager.map((o) => o.name).join(', ')} ${noManager.length === 1 ? 'has' : 'have'} no active manager and will be recorded as failed.`}
                      </>}
              </span>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button type="button" className="btn-cancel" disabled={busy} onClick={() => setCompose(null)}>Cancel</button>
              <button type="submit" className="export-button" disabled={busy || !preview?.organisations?.length}>
                <FaPaperPlane style={{ marginRight: 5 }} /> {busy ? 'Sending…' : 'Send'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default OrgMessages;
